import { TablePropertiesIcon } from 'lucide-react'

export enum OptionSettingTab {
  COLUMN = 'COLUMN',
}

export const OPTION_SETTING_TAB = [
  {
    id: OptionSettingTab.COLUMN,
    label: 'Columns',
    icon: TablePropertiesIcon,
  },
]

// Option table column visibility configuration
// Based on IncrementalTicker type fields from types/generated/common/types.ts
export type OptionTableColumn = {
  // Position (special column)
  position: boolean // Shows position quantity and PnL

  // Core Pricing
  bid: boolean
  ask: boolean
  sizeBid: boolean // bestBidQuantity
  sizeAsk: boolean // bestAskQuantity
  lastPrice: boolean
  mark: boolean // markPrice
  indexPrice: boolean
  settlementPrice: boolean
  underlyingPrice: boolean

  // Open Interest
  oi: boolean // openInterest

  // Greeks (from Greeks interface)
  delta: boolean
  gamma: boolean
  theta: boolean
  vega: boolean
  rho: boolean

  // Implied Volatility
  markIv: boolean
  bidIv: boolean
  askIv: boolean

  // Limits
  minPrice: boolean
  maxPrice: boolean

  // 24h Stats (from TickerStats interface)
  high24h: boolean // stats.high
  low24h: boolean // stats.low
  priceChange24h: boolean // stats.priceChangePercent
  volume: boolean // stats.volumeBase
}

// Column metadata for option table
// name: full name for dialog display
// abbr: abbreviated name for table headers
// description: tooltip content
// NOTE: The order of keys defines the Call side column order (left to right toward strike)
export type OptionColumnMeta = {
  name: string
  abbr: string
  description?: string
}

// Keys are ordered for Call side: Position -> OI -> Greeks -> IV -> Pricing -> Stats -> Limits -> Core (near strike)
export const OPTION_TABLE_COLUMNS: Record<keyof OptionTableColumn, OptionColumnMeta> = {
  // Open Interest
  oi: {
    name: 'Open Interest',
    abbr: 'OI',
    description: 'Open Interest - total number of outstanding contracts',
  },

  // Greeks
  delta: {
    name: 'Delta',
    abbr: 'Delta',
    description:
      'Approximates the probability of expiring in-the-money and shows how much the option price moves relative to the underlying',
  },
  gamma: {
    name: 'Gamma',
    abbr: 'Gamma',
    description: 'Rate of change of delta per 1 point move in the underlying',
  },
  theta: {
    name: 'Theta',
    abbr: 'Theta',
    description: 'Time decay - how much value the option loses per day',
  },
  vega: {
    name: 'Vega',
    abbr: 'Vega',
    description: 'Sensitivity to a 1% change in implied volatility',
  },
  rho: {
    name: 'Rho',
    abbr: 'Rho',
    description: 'Sensitivity to a 1% change in interest rate',
  },

  // Implied Volatility
  markIv: {
    name: 'Mark IV',
    abbr: 'Mark IV',
    description: 'Mark Implied Volatility - the expected future volatility priced into the option',
  },
  bidIv: {
    name: 'Bid IV',
    abbr: 'Bid IV',
    description: 'Implied Volatility at the bid price',
  },
  askIv: {
    name: 'Ask IV',
    abbr: 'Ask IV',
    description: 'Implied Volatility at the ask price',
  },

  // Pricing
  lastPrice: {
    name: 'Last Price',
    abbr: 'Last',
    description: 'The most recent trade price for this option',
  },
  indexPrice: {
    name: 'Index Price',
    abbr: 'Index',
    description: 'The underlying index price',
  },
  settlementPrice: {
    name: 'Settlement Price',
    abbr: 'Settle',
    description: 'The official settlement price for the option',
  },
  underlyingPrice: {
    name: 'Underlying Price',
    abbr: 'Undl',
    description: 'Current price of the underlying asset',
  },

  // 24h Stats
  high24h: {
    name: '24h High',
    abbr: 'H 24h',
    description: 'Highest price in the last 24 hours',
  },
  low24h: {
    name: '24h Low',
    abbr: 'L 24h',
    description: 'Lowest price in the last 24 hours',
  },
  priceChange24h: {
    name: '24h Change %',
    abbr: 'Chg 24h',
    description: 'Price change percentage in the last 24 hours',
  },
  volume: {
    name: 'Volume',
    abbr: 'Vol',
    description: 'Trading volume in the last 24 hours',
  },

  // Limits
  minPrice: {
    name: 'Min Price',
    abbr: 'Min',
    description: 'Minimum allowed order price',
  },
  maxPrice: {
    name: 'Max Price',
    abbr: 'Max',
    description: 'Maximum allowed order price',
  },

  // Core Pricing (always near strike) - order: sizeAsk, ask, mark, bid, sizeBid
  sizeBid: {
    name: 'Size (Bid)',
    abbr: 'Size',
    description: 'Available quantity at the best bid price',
  },
  bid: {
    name: 'Bid',
    abbr: 'Bid',
    description: 'The highest price a buyer is willing to pay for this option right now',
  },
  mark: {
    name: 'Mark Price',
    abbr: 'Mark',
    description:
      'The estimated fair value of the option, typically the midpoint between bid and ask prices',
  },
  ask: {
    name: 'Ask',
    abbr: 'Ask',
    description: 'The lowest price a seller is willing to accept for this option right now',
  },
  sizeAsk: {
    name: 'Size (Ask)',
    abbr: 'Size',
    description: 'Available quantity at the best ask price',
  },
  // Position (special column - always at end for Calls, start for Puts)
  position: {
    name: 'Position',
    abbr: 'Pos',
    description: 'Shows your position quantity for this option',
  },
}

// IV columns are not rendered as standalone columns (shown as sub-text in bid/ask/mark cells)
const IV_COLUMNS: (keyof OptionTableColumn)[] = ['markIv', 'bidIv', 'askIv']

// Column order derived from OPTION_TABLE_COLUMNS key order
// Call side: left to right toward strike
export const CALL_COLUMN_ORDER = (
  Object.keys(OPTION_TABLE_COLUMNS) as (keyof OptionTableColumn)[]
).filter((key) => !IV_COLUMNS.includes(key))
// Put side: reverse of Call side (mirror layout), but keep sizeBid → bid → mark → ask → sizeAsk left-to-right
export const PUT_COLUMN_ORDER: (keyof OptionTableColumn)[] = (() => {
  const reversed = [...CALL_COLUMN_ORDER].reverse()
  const bidIdx = reversed.indexOf('bid')
  const askIdx = reversed.indexOf('ask')
  if (bidIdx !== -1 && askIdx !== -1) {
    ;[reversed[bidIdx], reversed[askIdx]] = [reversed[askIdx], reversed[bidIdx]]
  }
  const sizeBidIdx = reversed.indexOf('sizeBid')
  const sizeAskIdx = reversed.indexOf('sizeAsk')
  if (sizeBidIdx !== -1 && sizeAskIdx !== -1) {
    ;[reversed[sizeBidIdx], reversed[sizeAskIdx]] = [reversed[sizeAskIdx], reversed[sizeBidIdx]]
  }
  return reversed
})()

// Default visible columns (current behavior)
export const DEFAULT_OPTION_COLUMNS: OptionTableColumn = {
  // Position - default ON
  position: true,

  // Core Pricing - defaults ON
  bid: true,
  ask: true,
  sizeBid: true,
  sizeAsk: true,
  lastPrice: false,
  mark: true,
  indexPrice: false,
  settlementPrice: false,
  underlyingPrice: false,

  // Open Interest - default ON
  oi: true,

  // Greeks - only delta ON by default
  delta: true,
  gamma: false,
  theta: false,
  vega: false,
  rho: false,

  // Implied Volatility - defaults OFF
  markIv: false,
  bidIv: false,
  askIv: false,

  // Limits - defaults OFF
  minPrice: false,
  maxPrice: false,

  // 24h Stats - defaults OFF
  high24h: false,
  low24h: false,
  priceChange24h: false,
  volume: false,
}

// Column grouping for better UI organization in dialog
export const OPTION_COLUMN_GROUPS = {
  position: {
    label: 'Position',
    columns: ['position'] as const,
  },
  pricing: {
    label: 'Pricing',
    columns: [
      'bid',
      'ask',
      'sizeBid',
      'sizeAsk',
      'mark',
      'lastPrice',
      'indexPrice',
      'settlementPrice',
      'underlyingPrice',
    ] as const,
  },
  interest: {
    label: 'Interest',
    columns: ['oi'] as const,
  },
  greeks: {
    label: 'Greeks',
    columns: ['delta', 'gamma', 'theta', 'vega', 'rho'] as const,
  },
  limits: {
    label: 'Limits',
    columns: ['minPrice', 'maxPrice'] as const,
  },
  stats: {
    label: '24h Stats',
    columns: ['high24h', 'low24h', 'priceChange24h', 'volume'] as const,
  },
}

export const DEFAULT_OPTION_COLUMNS_ORDER: number[] = Object.keys(OPTION_TABLE_COLUMNS).map(
  (_, index) => index
)
